import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { useDropzone } from "react-dropzone"
import { COUNTRIES } from "../../constants/countries"
import { contactFormSubmit } from "./../../utils/contact-form-submit"
import { useForm, useWatch } from "react-hook-form"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import ReCAPTCHA from "react-google-recaptcha"
import { getLanguage, getTranslation } from "../../utils"

const StyledContactForm = styled.form`
  position: relative;
  margin: 0;
`

const StyledRow = styled.div`
  display: flex;
  flex-direction: column;
  ${props =>
    props.isCenter &&
    `
    justify-content: center;
  `}

  @media (min-width: 769px) {
    flex-direction: row;
    padding-bottom: 2rem;

    &:last-child {
      padding-bottom: 0;
    }

    > div {
      &:first-child {
        padding-right: 3rem;
      }

      &:last-child {
        padding-left: 3rem;
      }
    }

    ${props =>
      props.isFull &&
      `
        > div {
          width:100%;

          &:first-child,
          &:last-child{
            padding: 0;
          }
        }
      `};
  }
`

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 1rem;

  @media (min-width: 769px) {
    flex-direction: ${props => (props.isRow ? "row" : "column")};
    width: 50%;
    padding-bottom: 0;
  }

  input,
  textarea,
  select {
    width: 100%;
    color: #767676;
    font-size: 1.3rem;
    text-align: center;
    border: 0.1rem solid #767676;
    border-radius: 0.4rem;
    transition: 200ms linear;
    -webkit-appearance: none;

    @media (min-width: 769px) {
      font-size: 1.8rem;
    }

    &:focus,
    &:active {
      outline: none;
      border: 0.2rem solid #767676;
    }
  }

  input,
  select {
    height: 4rem;
  }

  select {
    text-align: center;
    text-align-last: center;
  }

  textarea {
    min-height: 20rem;
    padding: 1rem;
    resize: none;
    text-align: left;
  }

  p {
    font-size: 1.1rem;
    padding-top: 1rem;
    margin-bottom: 0;

    @media (min-width: 769px) {
      font-size: 1.2rem;
      margin-bottom: 1rem;

      &.file-info,
      &.upload-info {
        padding-top: 0;
      }
    }

    @media (max-width: 769px) {
      &.file-info {
        font-weight: bold;
        margin-bottom: 0;
        padding-top: 0;
      }
    }

    ${props =>
      props.isRow &&
      `
      &:first-child {
        min-width: 10rem;
        padding-right: 1rem;
      }
    `};

    @media (min-width: 769px) {
      span {
        display: block;
        font-size: 1.8rem;
        margin-bottom: 1rem;
      }
    }
  }

  .error-message {
    color: #b5161b;
  }
`

const StyledTermsMessage = styled.div`
  width: 100%;
  color: ${props => props.theme.crockpot.text};
  font-size: 1.2rem;
  padding-top: 1rem;

  @media (min-width: 769px) {
    text-align: center;
    font-size: 1.8rem;
    padding-top: 2rem;
  }

  a {
    color: ${props => props.theme.crockpot.text};
    font-weight: 700;
    text-decoration: none;

    @media (min-width: 769px) {
      margin: 0 0.2rem;
    }
  }
`

const StyledSubmitButton = styled.input`
  display: inline-block;
  font-family: ${props => props.theme.crockpot.secondFont};
  font-size: 1.2rem;
  text-transform: uppercase;
  color: ${props => props.theme.crockpot.text};
  background: ${props => props.theme.crockpot.main};
  min-width: 16rem;
  text-decoration: none;
  margin: 2rem auto 0;
  padding: 0.5rem 1rem;
  border-radius: 0.6rem;
  border: 0.2rem solid ${props => props.theme.crockpot.main};
  transition: all 200ms linear;
  cursor: pointer;

  @media (min-width: 769px) {
    font-size: 2rem;
    padding: 1rem 2rem;
  }

  &:hover {
    color: ${props => props.theme.crockpot.main};
    background: ${props => props.theme.crockpot.text};
    border-color: ${props => props.theme.crockpot.text};
  }
`

const UploadButton = styled.span`
  display: inline-block;
  overflow: hidden;
  width: auto;
  max-width: 30rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${props => props.theme.crockpot.text};
  font-size: 1.2rem;
  font-family: ${props => props.theme.crockpot.secondFont};
  text-transform: uppercase;
  padding: 0.9rem 3rem;
  border: 0.1rem solid ${props => props.theme.crockpot.text};
  background: none;
  border-radius: 0.6rem;
  cursor: pointer;
  transition: 200ms linear;

  &:hover {
    color: #fff;
    background-color: ${props => props.theme.crockpot.text};
  }

  @media (min-width: 769px) {
    font-size: 2rem;
  }
`

const Dropzone = styled.div`
  outline: none;
  margin-top: 0.5rem;
`

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 5rem;
  color: #666161;
  background-color: rgba(255, 255, 255, 0.6);
  z-index: 1;

  @keyframes rotating {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    animation: rotating 2s linear infinite;
  }
`

const MessageWidth = control => {
  const message = useWatch({
    control,
    name: "WebFormDetails.KeyValuePairs.comments",
  })

  return message ? message.length : 0
}

const ContactForm = ({ contactFormText, defaultRecaptchaToken }) => {
  const [csrfToken, setCsrfToken] = useState("token")
  const [recaptchaToken, setRecaptchaToken] = useState(defaultRecaptchaToken)
  const [fileData, setFileData] = useState()
  const [submitted, setSubmitted] = useState(false)
  const [failed, setFailed] = useState(false)
  const [emptyCaptcha, setEmptyCaptcha] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const recaptcha = useRef()
  const locale = process.env.GATSBY_LOCALE
  const lang = getLanguage()
  const defaultLocale =
    locale === "ch" || locale === "CH"
      ? lang.includes("/")
        ? lang.split("/")[1]
        : lang
      : locale
  const getToken = async () => {
    try {
      const handleFormToken = await fetch(`/.netlify/functions/handle-form`)

      if (!handleFormToken || handleFormToken.status === 404) return

      const isResponseObject = handleFormToken instanceof Object
      const responseToken = isResponseObject && (await handleFormToken.json())
      setCsrfToken(isResponseObject ? responseToken.token : handleFormToken)
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    getToken()
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      WebFormDetails: {
        Country: defaultLocale === "en" ? "GB" : defaultLocale?.toUpperCase(),
      },
    },
  })

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxSize: 3000000,
    onDrop: files => setFileData(files[0]),
    accept: {
      "image/jpeg": [],
      "image/jpg": [],
      "image/png": [],
      "image/gif": [],
      "application/pdf": [],
      "text/plain": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
    },
  })

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })

  const onSubmit = async (formData, e) => {
    if (!recaptchaToken) {
      setEmptyCaptcha(true)
      return
    }

    setEmptyCaptcha(false)
    setIsLoading(true)

    const d = new Date(),
      month = d.getMonth() + 1,
      day = d.getDate(),
      year = d.getFullYear()

    const header = {
      Header: {
        Base: {
          CallingLanguage: locale,
          CallingLocale: `${locale}_${formData.WebFormDetails.Country}`,
          CallingApplication: "Crockpot",
          CreateCaseSystem: "SFDC",
          CallingDate: `${day}/${month}/${year}`,
          UserIdentification: formData.WebFormDetails.Email,
        },
      },
    }

    const fullData = { ...formData, ...header }

    fullData.WebFormDetails.KeyValuePairs.language = getTranslation("language")
    fullData.WebFormDetails.KeyValuePairs.locale = `${locale}_${formData.WebFormDetails.Country}`
    fullData.WebFormDetails.SendEmail = "true"
    fullData.WebFormDetails.Type = "Consumer_Default"
    fullData.WebFormDetails.attachmentsList = []

    if (fileData) {
      const base64 = await toBase64(fileData)

      const baseSplit = base64.split(",")[1]
      fullData.WebFormDetails.attachmentsList = [
        {
          fileBody: baseSplit,
          fileName: fileData.name,
          fileType: fileData.type,
        },
      ]
    }

    fullData.WebFormDetails.productList = {
      material: "",
      modelName: "",
      quantity: "",
    }

    try {
      const submit = await contactFormSubmit(
        `/.netlify/functions/handle-form/submit-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "text/html",
            "CSRF-Token": csrfToken,
            "Recaptcha-Token": recaptchaToken,
          },
          body: JSON.stringify(fullData),
        },
      )

      setIsLoading(false)

      if (submit.status === 200) {
        e.target.reset()
        setSubmitted(true)
        setFailed(false)
      } else {
        setSubmitted(false)
        setFailed(true)
      }
    } catch (e) {
      setIsLoading(false)
      setFailed(true)
    }
  }

  const verifyCallback = async () => {
    setRecaptchaToken(recaptcha.current.getValue())
  }

  return (
    <StyledContactForm
      onSubmit={handleSubmit(onSubmit)}
      data-testid="contact-form"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
    >
      <p hidden>
        <label>
          Don"t fill this out:{" "}
          <input name="bot-field" {...register("bot-field")} />
        </label>
        <input type="hidden" name="csrf-token" value={csrfToken} />
      </p>
      <StyledRow>
        <StyledInputContainer>
          <label htmlFor="FirstName" className="visually-hidden" required>
            ${getTranslation("first-name")}
          </label>
          <input
            type="text"
            id="FirstName"
            name="WebFormDetails.FirstName"
            placeholder={`${getTranslation("first-name")}*`}
            aria-invalid={errors.WebFormDetails?.FirstName ? "true" : "false"}
            {...register("WebFormDetails.FirstName", {
              required: true,
            })}
          />
          {errors.WebFormDetails?.FirstName && (
            <p className="error-message first">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
        <StyledInputContainer>
          <label htmlFor="LastName" className="visually-hidden" required>
            {`${getTranslation("last-name")}*`}
          </label>
          <input
            type="text"
            id="LastName"
            name="WebFormDetails.LastName"
            placeholder={`${getTranslation("last-name")}*`}
            {...register("WebFormDetails.LastName", {
              required: true,
            })}
            aria-invalid={errors.WebFormDetails?.LastName ? "true" : "false"}
          />
          {errors.WebFormDetails?.LastName && (
            <p className="error-message last">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
      </StyledRow>
      <StyledRow>
        <StyledInputContainer>
          <label htmlFor="PhoneNumber" className="visually-hidden" required>
            {`${getTranslation("phone")}*`}
          </label>
          <input
            type="text"
            id="PhoneNumber"
            name="WebFormDetails.PhoneNumber"
            placeholder={`${getTranslation("phone")}*`}
            {...register("WebFormDetails.PhoneNumber", {
              required: true,
            })}
            aria-invalid={errors.WebFormDetails?.PhoneNumber ? "true" : "false"}
          />
          {errors.WebFormDetails?.PhoneNumber && (
            <p className="error-message phone">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
        <StyledInputContainer>
          <label htmlFor="Email" className="visually-hidden" required>
            {`${getTranslation("e-mail")}*`}
          </label>
          <input
            type="text"
            id="Email"
            name="WebFormDetails.Email"
            placeholder={`${getTranslation("e-mail")}*`}
            {...register("WebFormDetails.Email", {
              required: true,
              pattern:
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
            })}
            aria-invalid={errors.WebFormDetails?.Email ? "true" : "false"}
          />
          {errors.WebFormDetails?.Email && (
            <p className="error-message email">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
      </StyledRow>
      <StyledRow isFull={true}>
        <StyledInputContainer>
          <label htmlFor="Country" className="visually-hidden" required>
            {`${getTranslation("country")}*`}
          </label>
          <select
            name="WebFormDetails.Country"
            id="Country"
            placeholder={`${getTranslation("country")}*`}
            {...register("WebFormDetails.Country", {
              required: true,
              validate: {
                positive: value => value !== "Country*",
              },
            })}
            aria-invalid={errors.WebFormDetails?.Country ? "true" : "false"}
          >
            <option className="placeholder">
              {`${getTranslation("country")}*`}
            </option>
            {COUNTRIES.map(country => (
              <option value={country.code} key={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          {errors.WebFormDetails?.Country && (
            <p className="error-message country">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
      </StyledRow>
      <StyledRow isFull={true}>
        <StyledInputContainer>
          <label htmlFor="Address1" className="visually-hidden" required>
            {`${getTranslation("address-1")}*`}
          </label>
          <input
            type="text"
            id="Address1"
            name="WebFormDetails.Address1"
            placeholder={`${getTranslation("address-1")}*`}
            {...register("WebFormDetails.Address1", {
              required: true,
            })}
            aria-invalid={errors.WebFormDetails?.Address1 ? "true" : "false"}
          />
          {errors.WebFormDetails?.Address1 && (
            <p className="error-message add">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
      </StyledRow>
      <StyledRow isFull={true}>
        <StyledInputContainer>
          <label htmlFor="email" className="visually-hidden">
            {`${getTranslation("address-2")}`}
          </label>
          <input
            type="text"
            id="Address2"
            name="WebFormDetails.Address2"
            placeholder={`${getTranslation("address-2")}`}
            {...register("WebFormDetails.Address2")}
          />
        </StyledInputContainer>
      </StyledRow>
      <StyledRow>
        <StyledInputContainer>
          <label htmlFor="City" className="visually-hidden" required>
            {`${getTranslation("city")}*`}
          </label>
          <input
            type="text"
            id="City"
            name="WebFormDetails.City"
            placeholder={`${getTranslation("city")}*`}
            {...register("WebFormDetails.City", {
              required: true,
            })}
            aria-invalid={errors.WebFormDetails?.City ? "true" : "false"}
          />
          {errors.WebFormDetails?.City && (
            <p className="error-message city">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
        <StyledInputContainer>
          <label htmlFor="PostalCode" className="visually-hidden" required>
            {`${getTranslation("postal-code")}*`}
          </label>
          <input
            type="text"
            id="PostalCode"
            name="WebFormDetails.PostalCode"
            placeholder={`${getTranslation("postal-code")}*`}
            {...register("WebFormDetails.PostalCode", {
              required: true,
            })}
            aria-invalid={errors.WebFormDetails?.PostalCode ? "true" : "false"}
          />
          {errors.WebFormDetails?.PostalCode && (
            <p className="error-message postal">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
      </StyledRow>
      <StyledRow>
        <StyledInputContainer>
          <label htmlFor="dateCode" className="visually-hidden" required>
            {`${getTranslation("date-code")}`}
          </label>
          <input
            type="text"
            id="dateCode"
            name="WebFormDetails.KeyValuePairs.dateCode"
            placeholder={`${getTranslation("date-code")}`}
            {...register("WebFormDetails.KeyValuePairs.dateCode")}
          />
          <p>{getTranslation("code-info")}</p>
        </StyledInputContainer>
        <StyledInputContainer>
          <label htmlFor="modelNo" className="visually-hidden">
            {`${getTranslation("model-number")}`}
          </label>
          <input
            type="text"
            id="modelNo"
            name="WebFormDetails.KeyValuePairs.modelNo"
            placeholder={`${getTranslation("model-number")}`}
            {...register("WebFormDetails.KeyValuePairs.modelNo")}
          />
        </StyledInputContainer>
      </StyledRow>
      <StyledRow isFull={true}>
        <StyledInputContainer>
          <label htmlFor="subject" className="visually-hidden" required>
            {`${getTranslation("subject")}*`}
          </label>
          <input
            type="text"
            id="subject"
            name="WebFormDetails.KeyValuePairs.subject"
            placeholder={`${getTranslation("subject")}*`}
            {...register("WebFormDetails.KeyValuePairs.subject", {
              required: true,
            })}
            aria-invalid={errors.WebFormDetails?.subject ? "true" : "false"}
          />
          {errors.WebFormDetails?.KeyValuePairs?.subject && (
            <p className="error-message subject">
              {getTranslation("required-field")}
            </p>
          )}
        </StyledInputContainer>
      </StyledRow>
      <StyledRow isFull={true}>
        <StyledInputContainer>
          <label htmlFor="comments" className="visually-hidden" required>
            {`${getTranslation("message")}*`}
          </label>
          <textarea
            type="text"
            id="comments"
            name="WebFormDetails.KeyValuePairs.comments"
            placeholder={`${getTranslation("message")}*`}
            {...register("WebFormDetails.KeyValuePairs.comments", {
              required: true,
              maxLength: 500,
            })}
            maxLength={500}
            aria-invalid={errors.WebFormDetails?.comments ? "true" : "false"}
          ></textarea>
          {errors.WebFormDetails?.KeyValuePairs?.comments && (
            <p className="error-message comment">
              {errors.WebFormDetails?.KeyValuePairs?.comments.type ===
              "required"
                ? getTranslation("required-field")
                : getTranslation("max-length")}
            </p>
          )}
          <p>
            {getTranslation("you-have")}{" "}
            {MessageWidth(control) > 500 ? 0 : 500 - MessageWidth(control)}{" "}
            {getTranslation("characters-limit")}
          </p>
        </StyledInputContainer>
      </StyledRow>
      <StyledRow>
        <StyledInputContainer isRow={true}>
          <p className="file-info">
            <span>{getTranslation("attach-file")}</span>
          </p>
          <p className="upload-info">
            <span>{getTranslation("upload-info")}</span>
            {getTranslation("upload-file-sizes")}
          </p>
        </StyledInputContainer>
        <StyledInputContainer>
          <Dropzone {...getRootProps({ className: "dropzone" })}>
            <input {...getInputProps()} />
            <UploadButton>
              {acceptedFiles.length > 0
                ? acceptedFiles[0].name
                : getTranslation("choose-file")}
            </UploadButton>
          </Dropzone>
        </StyledInputContainer>
      </StyledRow>
      {process.env.GATSBY_RECAPTCHA_SITEKEY && (
        <StyledRow isCenter={true}>
          <ReCAPTCHA
            sitekey={process.env.GATSBY_RECAPTCHA_SITEKEY}
            onChange={verifyCallback}
            ref={recaptcha}
          />
        </StyledRow>
      )}
      <StyledRow>
        <StyledTermsMessage data-testid="status-message">
          {emptyCaptcha ? (
            getTranslation("empty-captcha")
          ) : failed ? (
            getTranslation("failed")
          ) : submitted ? (
            getTranslation("success")
          ) : (
            <>{documentToReactComponents(JSON.parse(contactFormText.raw))}</>
          )}
        </StyledTermsMessage>
      </StyledRow>
      <StyledRow>
        <StyledSubmitButton type="submit" value={getTranslation("submit")} />
      </StyledRow>
      {isLoading && (
        <LoadingOverlay>
          <FontAwesomeIcon icon={faSpinner} />
        </LoadingOverlay>
      )}
    </StyledContactForm>
  )
}

export default ContactForm
